import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { PoolsPageUserSettings, DateFilters } from "user/pools-page-user-settings";
import { Pool } from "interfaces/pool";
import { ParentPool } from "interfaces/parent-pool";
import { USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME } from "utils/constants";
import { UserSettings } from "user/user-settings";
import { ContestsPageUserSettings } from "user/contests-page-user-settings";
import { defaultContestDateFilters } from "utils/contest-filters";
import { EventsPageUserSettings } from "user/events-page-user-settings";
import { WagernetEventsPageUserSettings } from "user/wagernet-events-page-user-settings";

// Default date filters
const defaultDateFilters: DateFilters = {
  startAtAfter: null,
  startAtBefore: null,
  endAtAfter: null,
  endAtBefore: null,
  closedAtAfter: null,
  closedAtBefore: null,
  createdAtAfter: null,
  createdAtBefore: null,
  updatedAtAfter: null,
  updatedAtBefore: null,
};

// Define default settings structures
const defaultPoolsPageSettings: PoolsPageUserSettings<Pool> = {
  // Existing filters
  statusFilter: null,
  activityNameFilter: null,
  eventFilter: null,
  contestFilter: null,
  sort: { property: "created", direction: "desc" },
  createdByUserId: null,

  // New filters
  name: null,
  hasWagernetEvent: null,
  statusIn: [],
  statusNotIn: [],
  dateFilters: defaultDateFilters,
};

const defaultParentPoolPageSettings: PoolsPageUserSettings<ParentPool> = {
  // Existing filters
  statusFilter: null,
  activityNameFilter: null,
  eventFilter: null,
  contestFilter: null,
  sort: { property: "created", direction: "desc" },
  createdByUserId: null,

  // New filters
  name: null,
  hasWagernetEvent: null,
  statusIn: [],
  statusNotIn: [],
  dateFilters: defaultDateFilters,
};

const defaultContestsPageSettings: ContestsPageUserSettings = {
  name: null,
  typeFilter: null,
  statusIn: [],
  statusNotIn: [],
  dateFilters: defaultContestDateFilters,
  sort: { property: "start_at", direction: "desc" },
};

const defaultEventsPageSettings: EventsPageUserSettings = {
  activityNameFilter: null,
  nameSearch: null,
  sort: null,
};

const defaultWagernetEventsPageSettings: WagernetEventsPageUserSettings = {
  discipline_uri: undefined,
  competition_uri: undefined,
  season_uri: undefined,
  stage_uri: undefined,
  conference_uri: undefined,
  ranked: undefined,
  event_type: undefined,
  status: undefined,
  name_like: undefined,
  start_date_from: undefined,
  start_date_to: undefined,
  pageSize: 20,
  visibleColumns: ["name", "date", "status", "event_type", "competitors"],
  sortProperty: "start_date",
  sortDirection: "asc",
};

const defaultUserSettings: UserSettings = {
  poolsPageSettings: defaultPoolsPageSettings,
  parentPoolPageSettings: defaultParentPoolPageSettings,
  contestPageSettings: defaultContestsPageSettings,
  eventsPageSettings: defaultEventsPageSettings,
  wagernetEventsPageSettings: defaultWagernetEventsPageSettings,
};

const loadInitialState: () => UserSettings = () => {
  const savedSettings = localStorage.getItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME);
  let loadedSettings = { ...defaultUserSettings }; // Start with defaults

  if (savedSettings) {
    try {
      const parsed = JSON.parse(savedSettings);
      // Merge saved settings over defaults, ensuring nested objects exist
      loadedSettings = {
        ...defaultUserSettings,
        poolsPageSettings: { 
          ...defaultPoolsPageSettings, 
          ...(parsed.poolsPageSettings || {}),
          // Ensure sort is not null (migration)
          sort: parsed.poolsPageSettings?.sort || defaultPoolsPageSettings.sort,
          // Ensure dateFilters is properly merged
          dateFilters: {
            ...defaultDateFilters,
            ...(parsed.poolsPageSettings?.dateFilters || {})
          },
          // Initialize new arrays if they don't exist
          statusIn: parsed.poolsPageSettings?.statusIn || [],
          statusNotIn: parsed.poolsPageSettings?.statusNotIn || [],
          // Ensure contestFilter is migrated
          contestFilter: parsed.poolsPageSettings?.contestFilter || null
        },
        parentPoolPageSettings: {
          ...defaultParentPoolPageSettings,
          ...(parsed.parentPoolPageSettings || {}),
          // Ensure sort is not null (migration)
          sort: parsed.parentPoolPageSettings?.sort || defaultParentPoolPageSettings.sort,
          // Ensure dateFilters is properly merged
          dateFilters: {
            ...defaultDateFilters,
            ...(parsed.parentPoolPageSettings?.dateFilters || {})
          },
          // Initialize new arrays if they don't exist
          statusIn: parsed.parentPoolPageSettings?.statusIn || [],
          statusNotIn: parsed.parentPoolPageSettings?.statusNotIn || [],
          // Ensure contestFilter is migrated
          contestFilter: parsed.parentPoolPageSettings?.contestFilter || null
        },
        contestPageSettings: {
          ...defaultContestsPageSettings,
          ...(parsed.contestPageSettings || {}),
          sort: parsed.contestPageSettings?.sort || defaultContestsPageSettings.sort,
          statusIn: parsed.contestPageSettings?.statusIn || [],
          statusNotIn: parsed.contestPageSettings?.statusNotIn || [],
          dateFilters: {
            ...defaultContestDateFilters,
            ...(parsed.contestPageSettings?.dateFilters || {})
          },
        },
        eventsPageSettings: { ...defaultEventsPageSettings, ...(parsed.eventsPageSettings || {}) },
        wagernetEventsPageSettings: { ...defaultWagernetEventsPageSettings, ...(parsed.wagernetEventsPageSettings || {}) },
      };
    } catch (e) {
      console.error("Failed to parse user settings from localStorage", e);
      // Fallback to defaults if parsing fails
    }
  }

  // Handle specific migrations or cleanups (like 'all' filter)
  if (loadedSettings.poolsPageSettings.statusFilter === "all") {
    loadedSettings.poolsPageSettings.statusFilter = null;
  }
  if (loadedSettings.parentPoolPageSettings.statusFilter === "all") {
    loadedSettings.parentPoolPageSettings.statusFilter = null;
  }
  // Add any future migrations for eventsPageSettings here if needed

  return loadedSettings;
};

export const getUserSettingsState = (state: RootState) => state.userSettings;

const initialState: UserSettings = loadInitialState();

const slice = createSlice({
  name: "userSettings",
  initialState: initialState,
  reducers: {
    updatePoolsPageSettings: (state, action: PayloadAction<Partial<PoolsPageUserSettings<Pool>>>) => {
      state.poolsPageSettings = { ...state.poolsPageSettings, ...action.payload };
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    },
    updateParentPoolsPageSettings: (state, action: PayloadAction<Partial<PoolsPageUserSettings<ParentPool>>>) => {
      state.parentPoolPageSettings = { ...state.parentPoolPageSettings, ...action.payload };
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    },
    updateContestsPageSettings: (state, action: PayloadAction<Partial<ContestsPageUserSettings>>) => {
      state.contestPageSettings = { ...state.contestPageSettings, ...action.payload };
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    },
    // Updated reducer to accept Partial<EventsPageUserSettings> for easier updates
    updateEventsPageSettings: (state, action: PayloadAction<Partial<EventsPageUserSettings>>) => {
      state.eventsPageSettings = { ...state.eventsPageSettings, ...action.payload };
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    },
    updateWagernetEventsPageSettings: (state, action: PayloadAction<Partial<WagernetEventsPageUserSettings>>) => {
      state.wagernetEventsPageSettings = { ...state.wagernetEventsPageSettings, ...action.payload };
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    },
    resetPoolsPageSettings: (state) => {
      state.poolsPageSettings = defaultPoolsPageSettings;
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    },
    resetParentPoolsPageSettings: (state) => {
      state.parentPoolPageSettings = defaultParentPoolPageSettings;
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    },
    resetContestsPageSettings: (state) => {
      state.contestPageSettings = defaultContestsPageSettings;
      localStorage.setItem(USER_SETTINGS_LOCAL_STORAGE_ITEM_NAME, JSON.stringify(state));
    }
  }
});

export default slice.reducer;
export const {
  updatePoolsPageSettings,
  updateParentPoolsPageSettings,
  updateContestsPageSettings,
  updateEventsPageSettings,
  updateWagernetEventsPageSettings,
  resetPoolsPageSettings,
  resetParentPoolsPageSettings,
  resetContestsPageSettings
} = slice.actions;
